/**
 * Clerk appearance — layer ownership (Clerk Core 2+):
 *
 *   rootBox  → layout wrapper only (transparent, no chrome)
 *   cardBox  → pass-through wrapper around card + footer (transparent)
 *   card     → single visible panel (bg, shadow, border, padding)
 *   footer   → below card, on panel background (transparent)
 */
import { useMemo } from 'react';
import { useTheme } from '../components/context/ThemeContext';

const BRAND_PRIMARY = '#0051B0';

export function useClerkAppearance() {
  const { isDark } = useTheme();

  return useMemo(() => {
    const panelBg = isDark ? '#1a1a1a' : '#ffffff';
    const borderColor = isDark ? '#404040' : '#E2E8F0';
    const cardShadow = isDark
      ? '0 4px 24px rgba(0, 0, 0, 0.35)'
      : '0 4px 24px rgba(0, 0, 0, 0.08)';

    return {
      variables: {
        colorPrimary: BRAND_PRIMARY,
        colorText: isDark ? '#ffffff' : '#090914',
        colorTextSecondary: isDark ? '#b8b8b8' : '#52525B',
        colorBackground: panelBg,
        colorInputBackground: isDark ? '#2d2d2d' : '#F8FAFC',
        colorInputText: isDark ? '#ffffff' : '#090914',
        borderRadius: '10px',
        fontFamily: 'inherit',
      },
      elements: {
        /* Outermost — never a visible card */
        rootBox: {
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          margin: 0,
          padding: 0,
          backgroundColor: 'transparent',
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
          boxSizing: 'border-box',
        },
        /* Wraps card + footer — pass-through, not a second card */
        cardBox: {
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          margin: 0,
          padding: 0,
          backgroundColor: 'transparent',
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
          boxSizing: 'border-box',
        },
        /* The only visible white/dark panel */
        card: {
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          margin: 0,
          backgroundColor: panelBg,
          boxShadow: cardShadow,
          border: `1px solid ${borderColor}`,
          borderRadius: '10px',
          padding: '1.5rem',
          boxSizing: 'border-box',
        },
        main: {
          width: '100%',
          minWidth: 0,
          margin: 0,
          padding: 0,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          border: 'none',
          boxSizing: 'border-box',
        },
        footer: {
          margin: 0,
          padding: '1rem 0 0',
          backgroundColor: 'transparent',
          background: 'transparent',
          boxShadow: 'none',
          border: 'none',
        },
        footerPages: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
        formFieldRow: {
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.75rem',
          minWidth: 0,
          width: '100%',
          boxSizing: 'border-box',
        },
        formField: {
          flex: '1 1 11rem',
          minWidth: 0,
          boxSizing: 'border-box',
        },
        headerTitle: { display: 'none' },
        headerSubtitle: { display: 'none' },
        socialButtonsBlockButton: {
          borderRadius: '10px',
          border: `1px solid ${borderColor}`,
        },
        formButtonPrimary: {
          backgroundColor: BRAND_PRIMARY,
          borderRadius: '10px',
          fontWeight: 700,
        },
        formFieldInput: {
          borderRadius: '8px',
          border: `1px solid ${borderColor}`,
          minHeight: '48px',
        },
        footerActionLink: { color: BRAND_PRIMARY },
        identityPreviewEditButton: { color: BRAND_PRIMARY },
      },
    };
  }, [isDark]);
}
